import React from 'react'
import { Link } from '@inertiajs/react'
import GuideLayout from '@/components/help/GuideLayout'
import GuideSection from '@/components/help/GuideSection'
import GuideWidget from '@/components/help/GuideWidget'
import GuideTip from '@/components/help/GuideTip'
import GuideRelatedLinks from '@/components/help/GuideRelatedLinks'
import ImpactLevelCompare from '@/components/help/widgets/ImpactLevelCompare'
import RampCurveCompare from '@/components/help/widgets/RampCurveCompare'
import type { SharedProps } from '@/types/shared'

export default function EventsGuide({ currentUser, properties }: Pick<SharedProps, 'currentUser' | 'properties'>) {
  return (
    <GuideLayout
      currentUser={currentUser}
      properties={properties}
      title="Events"
      subtitle="Capture demand spikes from local events that your booking history hasn't seen before."
    >
      <GuideSection title="Why Event-Based Pricing?">
        <p className="text-sm leading-relaxed text-muted-foreground">
          A music festival, a conference, a university graduation, a major sports match -- these create
          demand spikes that your historical baseline cannot predict because they may be new or one-off.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Events give the engine an <strong className="text-foreground">explicit signal</strong>:
          "demand will be higher than normal on these dates." Without this signal, the engine only reacts{' '}
          <em>after</em> bookings start arriving -- by which time you may have already sold rooms too
          cheaply.
        </p>
      </GuideSection>

      <GuideSection title="How Events Work">
        <p className="text-sm leading-relaxed text-muted-foreground">
          Each event has three components:
        </p>
        <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            <strong className="text-foreground">A name</strong> -- something descriptive like "Glastonbury
            Festival" or "University Graduation Week"
          </li>
          <li>
            <strong className="text-foreground">A date range</strong> -- the start and end dates the event
            covers
          </li>
          <li>
            <strong className="text-foreground">An impact level</strong> -- controls how much the rate is
            boosted
          </li>
          <li>
            <strong className="text-foreground">A ramp (build-up & cool-down)</strong> -- gradually increases
            rates before the event and decreases after. Bigger events get longer ramps by default, but you
            can adjust this to match your property's experience.
          </li>
        </ul>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Events are applied at <strong className="text-foreground">stage 6 of 12</strong> in the pipeline
          (after demand response, before orphan day). The demand system measures booking pace against your
          event prediction — if bookings are slower than expected it gently eases the price, if faster it
          captures the upside. Your multiplier sets the target, demand fine-tunes around it. Events can be
          assigned to specific properties or applied across all properties.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          For a deeper look at how demand and events work together, including an interactive
          scenario explorer, see the{' '}
          <Link href="/guides/demand-response" className="text-primary hover:underline">
            Demand Response guide
          </Link>.
        </p>
      </GuideSection>

      <GuideSection title="Impact Levels">
        <p className="text-sm leading-relaxed text-muted-foreground">
          Four levels, each with a different rate boost:
        </p>
        <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            <strong className="text-foreground">Minor (+15%)</strong> -- small local events, farmer's
            markets, local sports fixtures. No ramp (cliff).
          </li>
          <li>
            <strong className="text-foreground">Moderate (+30%)</strong> -- mid-size events, regional
            conferences, school events. 1 day ramp before & after.
          </li>
          <li>
            <strong className="text-foreground">Major (+50%)</strong> -- significant events, music
            festivals, major sports events. 2 days ramp before & after.
          </li>
          <li>
            <strong className="text-foreground">Extreme (+100%)</strong> -- once-a-year events, major
            concerts, national sporting events in your area. 3 days ramp before & after.
          </li>
        </ul>

        <GuideWidget
          title="Impact Level Comparison"
          footnote="See how each impact level affects your rates."
        >
          <ImpactLevelCompare />
        </GuideWidget>
      </GuideSection>

      <GuideSection title="Creating and Managing Events">
        <p className="text-sm leading-relaxed text-muted-foreground">
          To create or manage events:
        </p>
        <ol className="ml-4 list-decimal space-y-2 text-sm text-muted-foreground">
          <li>
            Navigate to <strong className="text-foreground">Pricing Rules → Events</strong>
          </li>
          <li>
            Click <strong className="text-foreground">Add Event</strong> and fill in the name, date range,
            and impact level
          </li>
          <li>
            Save -- the event takes effect on the next rate computation
          </li>
        </ol>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Create events as soon as you hear about them -- even tentative ones. The ramp settings
          automatically handle early arrivals and late departures, so you don't need to manually
          extend date ranges. If an event turns out to be smaller than expected, downgrade its impact
          level or remove it.
        </p>
      </GuideSection>

      <GuideSection title="Understanding Ramp Curves">
        <p className="text-sm leading-relaxed text-muted-foreground">
          The ramp curve controls <em>how</em> rates build up before an event and ease down after.
          You have two options:
        </p>
        <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            <strong className="text-foreground">Linear</strong> -- rates increase by the same amount
            each day. Predictable and easy to understand. Good default for most events.
          </li>
          <li>
            <strong className="text-foreground">Ease-in</strong> -- rates start low and accelerate
            closer to the event. Mimics how demand actually builds -- a trickle of early arrivals,
            then a rush. Great for concerts, sports events, and anything with a "last-minute booking"
            pattern.
          </li>
        </ul>

        <GuideWidget
          title="Ramp Curve Comparison"
          footnote="Adjust the peak multiplier and ramp days to see how each curve behaves."
        >
          <RampCurveCompare />
        </GuideWidget>
      </GuideSection>

      <GuideSection title="Events vs Seasons">
        <p className="text-sm leading-relaxed text-muted-foreground">
          <strong className="text-foreground">Seasons</strong> are for recurring yearly patterns -- summer
          holidays, Christmas, shoulder months. They repeat predictably and apply the same modifier every
          year.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          <strong className="text-foreground">Events</strong> are for specific, often one-off or irregular
          occurrences -- a concert, a conference, a sports tournament. They may not happen again, or they
          may move dates next year.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          If a "special event" happens every year on the same dates with predictable demand, consider
          making it a <strong className="text-foreground">season</strong> instead. Seasons are simpler to
          maintain for recurring patterns.
        </p>
      </GuideSection>

      <GuideSection title="Tips">
        <div className="space-y-3">
          <GuideTip type="do">
            Add events proactively, as soon as you hear about them. Early pricing signals capture more
            revenue than reactive adjustments.
          </GuideTip>
          <GuideTip type="do">
            Start with the default ramp settings and observe. Check the rate calendar after your first
            event to see if the ramp matched actual demand.
          </GuideTip>
          <GuideTip type="do">
            Use the 'Ease-in' curve for events where most arrivals happen close to the start date
            (concerts, one-day conferences).
          </GuideTip>
          <GuideTip type="dont">
            Don't use events for recurring patterns like bank holidays. Use seasons for those -- they are
            designed for predictable, repeating demand.
          </GuideTip>
          <GuideTip type="dont">
            Don't set every event to Extreme. If a local market adds +100% to your rates, you will price
            out regular guests who are not attending the event.
          </GuideTip>
        </div>
      </GuideSection>

      <GuideRelatedLinks
        links={[
          { slug: 'demand-response', title: 'Demand Response' },
          { slug: 'seasons', title: 'Seasons' },
          { slug: 'rate-calendar', title: 'Rate Calendar' },
        ]}
      />
    </GuideLayout>
  )
}
